import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider, Navigate, useLocation, useParams } from "react-router-dom";
import "../global.css";
import { getSelectedOrg } from "./hooks/useSelectedOrg";
import {
  buildDocumentTemplateCreatePanePath,
  buildDocumentTemplatePanePath,
  buildEncounterSchemaCreatePanePath,
  buildEncounterSchemaPanePath,
  buildTemplateConfigurationPath,
} from "./utils/templateRoutes";

// Redirect components for legacy URLs
function ConsultationRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/encounters/${id}`} replace />;
}

function CallRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/encounters/${id}`} replace />;
}

// Redirect for encounters/old org route
function EncounterOrgRedirect() {
  const { orgid, eid } = useParams<{ orgid: string; eid: string }>();
  const location = useLocation();
  if (eid === "new" || eid === "record" || eid === "upload") {
    return <Navigate to={`/o/${orgid}/encounters/start${location.search}`} replace />;
  }
  return <Navigate to={`/encounters/${eid}`} replace />;
}

function OrgConfigurationIndexRedirect() {
  const { orgid } = useParams<{ orgid: string }>();
  const location = useLocation();
  return <Navigate to={`/o/${orgid}/configuration/encounter-types${location.search}`} replace />;
}

function OrgTemplatesBrowseRedirect() {
  const { orgid } = useParams<{ orgid: string }>();
  const location = useLocation();
  const nextParams = new URLSearchParams(location.search);
  if (nextParams.get("deleted") === "true") {
    nextParams.set("showDeleted", "1");
  }
  nextParams.delete("deleted");
  const query = nextParams.toString();
  return <Navigate to={`/o/${orgid}/configuration/encounter-types${query ? `?${query}` : ""}`} replace />;
}

function OrgAnalyticsBrowseRedirect() {
  const { orgid } = useParams<{ orgid: string }>();
  const location = useLocation();
  const currentParams = new URLSearchParams(location.search);
  const nextParams = new URLSearchParams();
  const analyticsModelId = currentParams.get("analyticsModelId");
  if (analyticsModelId) {
    nextParams.set("selected", analyticsModelId);
  }
  if (currentParams.get("deleted") === "true") {
    nextParams.set("showDeleted", "1");
  }
  const query = nextParams.toString();
  return <Navigate to={`/o/${orgid}/configuration/analytics${query ? `?${query}` : ""}`} replace />;
}

function OrgConfigurationDocumentsEditorRedirect() {
  const { orgid, id } = useParams<{ orgid: string; id?: string }>();
  const location = useLocation();
  const nextParams = new URLSearchParams(location.search);
  if (location.pathname.endsWith("/configuration/documents")) {
    const selectedTemplateId = nextParams.get("selected");
    if (selectedTemplateId) {
      nextParams.delete("selected");
      nextParams.set("documentId", selectedTemplateId);
      nextParams.set("documentMode", "view");
    }
    nextParams.set("documents", "all");
  } else if (id) {
    nextParams.set("documentId", id);
    nextParams.set("documentMode", location.pathname.endsWith("/edit") ? "edit" : "view");
    nextParams.set("documents", "all");
  } else {
    nextParams.set("editor", "new-document");
  }
  const query = nextParams.toString();
  return <Navigate to={`/o/${orgid}/configuration/encounter-types${query ? `?${query}` : ""}`} replace />;
}

function OrgConfigurationAnalyticsEditorRedirect() {
  const { orgid, id } = useParams<{ orgid: string; id?: string }>();
  const location = useLocation();
  const nextParams = new URLSearchParams(location.search);
  nextParams.set("editor", id ? "edit" : "new");
  if (id) {
    nextParams.set("selected", id);
  }
  const query = nextParams.toString();
  return <Navigate to={`/o/${orgid}/configuration/analytics${query ? `?${query}` : ""}`} replace />;
}

function OrgConfigurationEncounterTypesEditorRedirect() {
  const { orgid, id } = useParams<{ orgid: string; id?: string }>();
  const location = useLocation();
  const nextParams = new URLSearchParams(location.search);
  nextParams.set("editor", id ? "edit" : "new");
  if (id) {
    nextParams.set("selected", id);
  }
  const query = nextParams.toString();
  return <Navigate to={`/o/${orgid}/configuration/encounter-types${query ? `?${query}` : ""}`} replace />;
}

function LegacyDocumentTemplateRedirect({ mode }: { mode: "view" | "edit" }) {
  const { id } = useParams<{ id: string }>();
  const orgid = getSelectedOrg();
  if (!orgid || !id) {
    return <Navigate to="/o/select" replace />;
  }
  return <Navigate to={buildDocumentTemplatePanePath(orgid, id, mode)} replace />;
}

function LegacyEncounterSchemaRedirect() {
  const { id } = useParams<{ id: string }>();
  const orgid = getSelectedOrg();
  if (!orgid || !id) {
    return <Navigate to="/o/select" replace />;
  }
  return <Navigate to={buildEncounterSchemaPanePath(orgid, id)} replace />;
}

function AdminDocumentTemplatesRedirect() {
  const { id } = useParams<{ id?: string }>();
  const orgid = getSelectedOrg();
  if (!orgid) {
    return <Navigate to="/o/select" replace />;
  }
  if (!id) {
    return <Navigate to={buildTemplateConfigurationPath(orgid)} replace />;
  }
  if (id === "new") {
    return <Navigate to={buildDocumentTemplateCreatePanePath(orgid)} replace />;
  }
  return <Navigate to={buildDocumentTemplatePanePath(orgid, id, "edit")} replace />;
}

function AdminEncounterSchemasRedirect() {
  const { id } = useParams<{ id?: string }>();
  const orgid = getSelectedOrg();
  if (!orgid) {
    return <Navigate to="/o/select" replace />;
  }
  if (!id) {
    return <Navigate to={buildTemplateConfigurationPath(orgid)} replace />;
  }
  if (id === "new") {
    return <Navigate to={buildEncounterSchemaCreatePanePath(orgid)} replace />;
  }
  return <Navigate to={buildEncounterSchemaPanePath(orgid, id)} replace />;
}

function OrgDocumentTemplateRouteRedirect({ mode }: { mode: "create" | "view" | "edit" }) {
  const { orgid, id } = useParams<{ orgid: string; id?: string }>();
  const location = useLocation();
  const currentParams = new URLSearchParams(location.search);
  if (!orgid) {
    return <Navigate to="/o/select" replace />;
  }
  if (mode === "create") {
    return (
      <Navigate
        to={buildDocumentTemplateCreatePanePath(orgid, currentParams.get("schema_id"), currentParams)}
        replace
      />
    );
  }
  if (!id) {
    return <Navigate to={buildTemplateConfigurationPath(orgid, currentParams)} replace />;
  }
  return <Navigate to={buildDocumentTemplatePanePath(orgid, id, mode, currentParams)} replace />;
}

function OrgEncounterSchemaRouteRedirect({ mode }: { mode: "create" | "edit" }) {
  const { orgid, id } = useParams<{ orgid: string; id?: string }>();
  const location = useLocation();
  const currentParams = new URLSearchParams(location.search);
  if (!orgid) {
    return <Navigate to="/o/select" replace />;
  }
  if (mode === "create") {
    return (
      <Navigate
        to={buildEncounterSchemaCreatePanePath(
          orgid,
          {
            createMode: currentParams.get("create_mode"),
            importedTemplateSeriesId: currentParams.get("imported_template_series_id"),
          },
          currentParams,
        )}
        replace
      />
    );
  }
  if (!id) {
    return <Navigate to={buildTemplateConfigurationPath(orgid, currentParams)} replace />;
  }
  return <Navigate to={buildEncounterSchemaPanePath(orgid, id, currentParams)} replace />;
}

function OrgMarketplaceRedirect() {
  const { orgid } = useParams<{ orgid: string }>();
  if (!orgid) {
    return <Navigate to="/o/select" replace />;
  }
  return <Navigate to={`/o/${orgid}/marketplace`} replace />;
}
import { setupCSSPreloadErrorHandler } from "./components/ErrorBoundary";
import { StandaloneErrorPage } from "./components/StandaloneErrorPage";
import { setupAssetErrorHandler } from "./utils/assetErrorHandler";
import { lazyWithRetry } from "./utils/lazyWithRetry";

const Layout = lazyWithRetry(() => import("./components/Layout"), "Layout");
const AcceptOrganizationInvitePage = lazyWithRetry(
  () => import("./pages/AcceptOrganizationInvitePage"),
  "AcceptOrganizationInvitePage",
);
const AdminAllEncountersPage = lazyWithRetry(
  () => import("./pages/AdminAllEncountersPage"),
  "AdminAllEncountersPage",
);
const AdminAllOrganizationsPage = lazyWithRetry(
  () => import("./pages/AdminAllOrganizationsPage"),
  "AdminAllOrganizationsPage",
);
const AdminOrganizationPage = lazyWithRetry(
  () => import("./pages/AdminOrganizationPage"),
  "AdminOrganizationPage",
);
const CompletePasswordResetPage = lazyWithRetry(
  () => import("./pages/CompletePasswordResetPage"),
  "CompletePasswordResetPage",
);
const EncounterAnalyticsPage = lazyWithRetry(
  () => import("./pages/EncounterAnalyticsPage"),
  "EncounterAnalyticsPage",
);
const EncounterAnalyticsSettingsPage = lazyWithRetry(
  () => import("./pages/EncounterAnalyticsSettingsPage"),
  "EncounterAnalyticsSettingsPage",
);
const EncounterCardsPage = lazyWithRetry(
  () => import("./pages/EncounterCardsPage"),
  "EncounterCardsPage",
);
const StaffAnalyticsPage = lazyWithRetry(
  () => import("./pages/StaffAnalyticsPage"),
  "StaffAnalyticsPage",
);
const EncounterDetailPage = lazyWithRetry(
  () => import("./pages/EncounterDetailPage"),
  "EncounterDetailPage",
);
const EncountersListPage = lazyWithRetry(
  () => import("./pages/EncountersListPage"),
  "EncountersListPage",
);
const EncounterStartPage = lazyWithRetry(
  () => import("./pages/EncounterStartPage"),
  "EncounterStartPage",
);
const ConsultationInitPage = lazyWithRetry(
  () => import("./pages/ConsultationInitPage"),
  "ConsultationInitPage",
);
const CreateOrganizationPage = lazyWithRetry(
  () => import("./pages/CreateOrganizationPage"),
  "CreateOrganizationPage",
);
const EmptyOrgPage = lazyWithRetry(() => import("./pages/EmptyOrgPage"), "EmptyOrgPage");
const HomePage = lazyWithRetry(() => import("./pages/HomePage"), "HomePage");
const LoginPage = lazyWithRetry(() => import("./pages/LoginPage"), "LoginPage");
const LogoutPage = lazyWithRetry(() => import("./pages/LogoutPage"), "LogoutPage");
const OrganizationDirectRoutePage = lazyWithRetry(
  () => import("./pages/OrganizationDirectRoutePage"),
  "OrganizationDirectRoutePage",
);
const OrganizationPage = lazyWithRetry(
  () => import("./pages/OrganizationPage"),
  "OrganizationPage",
);
const ResetPasswordPage = lazyWithRetry(
  () => import("./pages/ResetPasswordPage"),
  "ResetPasswordPage",
);
const SelectOrgPage = lazyWithRetry(() => import("./pages/SelectOrgPage"), "SelectOrgPage");
const SignupPage = lazyWithRetry(() => import("./pages/SignupPage"), "SignupPage");

// Template Management (EncounterSchemas)
const EncounterSchemasPage = lazyWithRetry(
  () => import("./pages/admin/templates/EncounterTemplatesPage"),
  "EncounterSchemasPage",
);
const EncounterSchemaEditorPage = lazyWithRetry(
  () => import("./pages/admin/templates/EncounterTemplateEditorPage"),
  "EncounterSchemaEditorPage",
);

// Template Management (DocumentTemplates)
const DocumentTemplatesPage = lazyWithRetry(
  () => import("./pages/admin/templates/DocumentTemplatesPage"),
  "DocumentTemplatesPage",
);
const DocumentTemplateEditorPage = lazyWithRetry(
  () => import("./pages/admin/templates/DocumentTemplateEditorPage"),
  "DocumentTemplateEditorPage",
);
const DocumentTemplateViewPage = lazyWithRetry(
  () => import("./pages/DocumentTemplateViewPage"),
  "DocumentTemplateViewPage",
);
const StandaloneDocumentTemplateEditorPage = lazyWithRetry(
  () => import("./pages/DocumentTemplateEditorPage"),
  "StandaloneDocumentTemplateEditorPage",
);

// Template Management (TagTaxonomies)
const AnalyticsModelsPage = lazyWithRetry(
  () => import("./pages/admin/templates/TagTaxonomiesPage"),
  "AnalyticsModelsPage",
);

// Organization-scoped Template Management
const OrgCommunityTemplatesPage = lazyWithRetry(
  () => import("./pages/org/OrgCommunityTemplatesPage"),
  "OrgCommunityTemplatesPage",
);
const OrgMarketplacePage = lazyWithRetry(
  () => import("./pages/org/OrgMarketplacePage"),
  "OrgMarketplacePage",
);
const OrgOnboardingPage = lazyWithRetry(
  () => import("./pages/org/OrgOnboardingPage"),
  "OrgOnboardingPage",
);
const OrgDocumentTemplateEditorPage = lazyWithRetry(
  () => import("./pages/org/OrgDocumentTemplateEditorPage"),
  "OrgDocumentTemplateEditorPage",
);
const OrgEncounterSchemaEditorPage = lazyWithRetry(
  () => import("./pages/org/OrgEncounterSchemaEditorPage"),
  "OrgEncounterSchemaEditorPage",
);
const OrgConfigurationPage = lazyWithRetry(
  () => import("./pages/org/OrgConfigurationPage"),
  "OrgConfigurationPage",
);

// Standalone (org-agnostic) Schema Editor
const StandaloneEncounterSchemaEditorPage = lazyWithRetry(
  () => import("./pages/EncounterSchemaEditorPage"),
  "StandaloneEncounterSchemaEditorPage",
);

const DashboardLayout = lazyWithRetry(
  () => import("./components/DashboardLayout"),
  "DashboardLayout",
);

// Set up error handlers for production asset loading
setupCSSPreloadErrorHandler();
setupAssetErrorHandler();

const rootElement = document.getElementById("root")!;
const root = ReactDOM.createRoot(rootElement);

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    errorElement: <StandaloneErrorPage />,
    children: [
      { index: true, element: <HomePage /> },
      { path: "signup", element: <SignupPage /> },
      { path: "login", element: <LoginPage /> },
      { path: "logout", element: <LogoutPage /> },
      { path: "password-reset", element: <ResetPasswordPage /> },
      {
        path: "complete-password-reset",
        element: <CompletePasswordResetPage />,
      },
      {
        path: "accept-organization-invite",
        element: <AcceptOrganizationInvitePage />,
      },
      { path: "create-organization", element: <CreateOrganizationPage /> },
      { path: "encounters/init", element: <ConsultationInitPage /> },
      // Legacy URL redirects
      // Deprecated old API entry point; use /encounters/init for new integrations.
      { path: "consultations/init", element: <ConsultationInitPage /> },
      { path: "consultations/:id", element: <ConsultationRedirect /> },
      { path: "calls/:id", element: <CallRedirect /> },
      { path: "templates/document/:id", element: <LegacyDocumentTemplateRedirect mode="view" /> },
      { path: "templates/document/:id/edit", element: <LegacyDocumentTemplateRedirect mode="edit" /> },
      { path: "templates/schema/:id", element: <LegacyEncounterSchemaRedirect /> },
      { path: "o", element: <EmptyOrgPage /> },
      {
        element: <DashboardLayout />,
        children: [
          { path: "encounters/:eid", element: <EncounterDetailPage /> },
          { path: "o/select", element: <SelectOrgPage /> },
          { path: "admin", element: <Navigate to="/admin/organizations" replace /> },
          { path: "admin/encounters", element: <AdminAllEncountersPage /> },
          { path: "admin/organizations", element: <AdminAllOrganizationsPage /> },
          {
            path: "admin/organizations/:orgid",
            element: <AdminOrganizationPage />,
          },
          {
            path: "admin/templates/encounter-schemas",
            element: <AdminEncounterSchemasRedirect />,
          },
          {
            path: "admin/templates/encounter-schemas/:id",
            element: <AdminEncounterSchemasRedirect />,
          },
          {
            path: "admin/templates/document-templates",
            element: <AdminDocumentTemplatesRedirect />,
          },
          {
            path: "admin/templates/document-templates/:id",
            element: <AdminDocumentTemplatesRedirect />,
          },
          {
            path: "admin/templates/analytics-models",
            element: <AnalyticsModelsPage />,
          },
          {
            path: "o/:orgid",
            children: [
              { path: "encounters", element: <EncountersListPage /> },
              { path: "encounters/start", element: <EncounterStartPage /> },
              { path: "encounters/:eid", element: <EncounterOrgRedirect /> },
              { path: "staff/:staffid", element: <StaffAnalyticsPage /> },
              { path: "staff/:staffid/analytics", element: <Navigate to=".." relative="path" replace /> },
              {
                path: "encounter-analytics",
                element: <EncounterAnalyticsPage />,
              },
              {
                path: "encounter-analytics/settings",
                element: <EncounterAnalyticsSettingsPage />,
              },
              {
                path: "analytics-models",
                element: <OrgAnalyticsBrowseRedirect />,
              },
              {
                path: "encounter-cards",
                element: <EncounterCardsPage />,
              },
              { path: "settings/:tab?", element: <OrganizationPage /> },
              { path: "marketplace", element: <OrgMarketplacePage /> },
              // Organization-scoped template management
              { path: "configuration", element: <OrgConfigurationIndexRedirect /> },
              { path: "configuration/encounter-types", element: <OrgConfigurationPage tab="encounter-types" /> },
              { path: "configuration/encounter-types/new", element: <OrgConfigurationEncounterTypesEditorRedirect /> },
              { path: "configuration/encounter-types/:id", element: <OrgConfigurationEncounterTypesEditorRedirect /> },
              { path: "configuration/documents", element: <OrgConfigurationDocumentsEditorRedirect /> },
              { path: "configuration/documents/new", element: <OrgConfigurationDocumentsEditorRedirect /> },
              { path: "configuration/documents/:id", element: <OrgConfigurationDocumentsEditorRedirect /> },
              { path: "configuration/documents/:id/edit", element: <OrgConfigurationDocumentsEditorRedirect /> },
              { path: "configuration/analytics", element: <OrgConfigurationPage tab="analytics" /> },
              { path: "configuration/analytics/new", element: <OrgConfigurationAnalyticsEditorRedirect /> },
              { path: "configuration/analytics/:id", element: <OrgConfigurationAnalyticsEditorRedirect /> },
              { path: "templates", element: <OrgTemplatesBrowseRedirect /> },
              { path: "templates/community", element: <OrgMarketplaceRedirect /> },
              { path: "templates/document/new", element: <OrgDocumentTemplateRouteRedirect mode="create" /> },
              { path: "templates/document/:id", element: <OrgDocumentTemplateRouteRedirect mode="view" /> },
              { path: "templates/document/:id/edit", element: <OrgDocumentTemplateRouteRedirect mode="edit" /> },
              { path: "templates/schema/new", element: <OrgEncounterSchemaRouteRedirect mode="create" /> },
              { path: "templates/schema/:id", element: <OrgEncounterSchemaRouteRedirect mode="edit" /> },
              { path: "onboarding", element: <OrgOnboardingPage /> },
              { index: true, element: <OrganizationDirectRoutePage /> },
            ],
          },
        ]
      }
    ],
  },
]);

root.render(
  <React.StrictMode>
    <Suspense fallback={null}>
      <RouterProvider router={router} />
    </Suspense>
  </React.StrictMode>,
);
